import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from './api';
import { Aviso, Campo, Area, Dialogo, ErroDaApi, ZonaDeArquivo } from './ui';

/**
 * O botão de relatar, presente em toda tela.
 *
 * Existe por causa de uma frase que se repete em todo beta:
 *
 *   "não consegui salvar aquela tela ontem"
 *
 * Faltam nela as cinco coisas que decidem se dá para consertar: qual
 * tela, qual usuário, que horas, qual versão e o que a pessoa tentava
 * fazer. Quem relatou não anotou porque não sabia que precisava; quem
 * conserta não pergunta porque já se passaram três dias.
 *
 * Então o formulário **não pergunta nada disso**. Ele captura sozinho:
 *
 *  - a rota e a URL completa;
 *  - o navegador e o tamanho da janela — metade das queixas de layout é
 *    uma janela estreita que ninguém pensou em mencionar;
 *  - a versão do pacote publicado, lida do `<script>` da própria
 *    página. É o que responde "isso ainda acontece?" sem depender de
 *    memória.
 *
 * À pessoa sobram as duas perguntas que só ela pode responder: **o que
 * você esperava** e **o que aconteceu**.
 *
 * O print entra por arquivo ou por **colar** (Ctrl+V): no Windows,
 * `PrtSc` + Ctrl+V é o caminho mais curto que existe entre ver o defeito
 * e relatá-lo, e todo passo a mais nesse caminho é um relato que não
 * acontece.
 */

const TIPOS = [
  { v: 'erro', t: 'Deu erro', d: 'algo falhou ou não salvou' },
  { v: 'fluxo', t: 'Fluxo estranho', d: 'funciona, mas não é assim que trabalhamos' },
  { v: 'tela', t: 'Tela / layout', d: 'algo cortado, torto ou ilegível' },
  { v: 'texto', t: 'Texto errado', d: 'nome, unidade ou termo incorreto' },
  { v: 'lentidao', t: 'Lentidão', d: 'demorou demais' },
  { v: 'sugestao', t: 'Sugestão', d: 'ideia de melhoria' },
] as const;

/**
 * Abre o diálogo do Relatar, de onde quer que seja chamado (onda 308, spec 06
 * §5.6, D4).
 *
 * No celular o botão flutuante some — ele cobria o canto da barra de ação —,
 * e quem abre o Relatar é o item «Relatar um problema nesta tela» da folha
 * «Mais». Por um aviso da janela, e não por um estado erguido até a
 * `Moldura`: o diálogo continua inteiro aqui, a `Moldura` continua com
 * `<Relatar />` (o 33.4 cobra), e quem chama não precisa saber onde ele mora.
 */
export function abrirRelatar(): void {
  window.dispatchEvent(new CustomEvent('labmanager:relatar'));
}

/** A versão publicada, lida do próprio pacote que está rodando. */
function versaoDoPacote(): string {
  try {
    const s = [...document.querySelectorAll('script[src]')]
      .map((e) => (e as HTMLScriptElement).src)
      .find((u) => /\/ativos\/index-/.test(u));
    return s ? s.split('/').pop()! : 'desconhecida';
  } catch { return 'desconhecida'; }
}

export function Relatar() {
  const local = useLocation();
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<string>('erro');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [print, setPrint] = useState<File | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  // O aviso de `abrirRelatar()` (a folha «Mais», no celular) abre o diálogo.
  useEffect(() => {
    const abrir = () => setAberto(true);
    window.addEventListener('labmanager:relatar', abrir);
    return () => window.removeEventListener('labmanager:relatar', abrir);
  }, []);

  // Miniatura do print, e limpeza do object URL — sem isso a memória
  // cresce a cada troca de imagem.
  useEffect(() => {
    if (!print) { setPrevia(null); return; }
    const u = URL.createObjectURL(print);
    setPrevia(u);
    return () => URL.revokeObjectURL(u);
  }, [print]);

  // Colar imagem direto no diálogo.
  useEffect(() => {
    if (!aberto) return;
    const aoColar = (e: ClipboardEvent) => {
      const item = [...(e.clipboardData?.items ?? [])]
        .find((i) => i.type.startsWith('image/'));
      const f = item?.getAsFile();
      if (f) { setPrint(f); e.preventDefault(); }
    };
    window.addEventListener('paste', aoColar);
    return () => window.removeEventListener('paste', aoColar);
  }, [aberto]);

  const fechar = () => {
    setAberto(false);
    setPronto(false);
    setErro(null);
  };

  async function enviar() {
    setEnviando(true); setErro(null);
    try {
      const f = new FormData();
      f.set('tipo', tipo);
      f.set('titulo', titulo.trim());
      f.set('descricao', descricao.trim());
      f.set('rota', local.pathname);
      f.set('url', window.location.href);
      f.set('navegador', navigator.userAgent.slice(0, 400));
      f.set('viewport', `${window.innerWidth}×${window.innerHeight}`);
      f.set('versao', versaoDoPacote());
      if (print) f.set('print', print);

      await api('/relatos', { metodo: 'POST', formulario: f });

      setPronto(true);
      setTitulo(''); setDescricao(''); setPrint(null); setTipo('erro');
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  }

  const invalido = titulo.trim().length < 5 || descricao.trim().length < 15;

  return (
    <>
      <button
        type="button"
        className="botao-relatar"
        onClick={() => setAberto(true)}
        title="Relatar um problema ou sugestão"
      >
        <span aria-hidden="true">✎</span>
        <span className="rotulo-relatar">Relatar</span>
      </button>

      {aberto && (
        <Dialogo aberto titulo="Relatar" aoFechar={fechar}>
          {pronto ? (
            <>
              <Aviso tipo="ok" titulo="Recebido">
                Obrigado. O relato entrou com a tela, a hora e a versão do
                sistema anexadas — quem for olhar não vai precisar
                perguntar nada disso.
              </Aviso>
              <div className="rodape-dialogo">
                <button type="button" className="secundario"
                  onClick={() => setPronto(false)}>Relatar outro</button>
                <button type="button" onClick={fechar}>Fechar</button>
              </div>
            </>
          ) : (
            <div ref={caixa}>
              <fieldset className="tipos-relato">
                <legend>O que é</legend>
                {TIPOS.map((t) => (
                  <label key={t.v} className={tipo === t.v ? 'escolhido' : undefined}>
                    <input
                      type="radio"
                      name="tipo-relato"
                      checked={tipo === t.v}
                      onChange={() => setTipo(t.v)}
                    />
                    <b>{t.t}</b>
                    <span className="sub">{t.d}</span>
                  </label>
                ))}
              </fieldset>

              <Campo
                rotulo="Em uma linha"
                dica="Como você contaria para um colega ao lado."
                value={titulo}
                maxLength={160}
                onChange={(e) => setTitulo(e.currentTarget.value)}
              />

              <Area
                rotulo="O que você esperava, e o que aconteceu"
                dica="Se souber refazer, diga os passos. É o que mais acelera o conserto."
                rows={5}
                value={descricao}
                maxLength={4000}
                onChange={(e) => setDescricao(e.currentTarget.value)}
              />

              <ZonaDeArquivo
                rotulo="Print da tela (opcional)"
                aceita="image/*"
                tipos="Imagem"
                arquivos={print}
                textoVazio="Arraste a imagem aqui"
                aoEscolher={(fs) => setPrint(fs[0] ?? null)}
              />
              <p className="dica">
                Ou tecle <kbd>PrtSc</kbd> e cole aqui com <kbd>Ctrl</kbd>+<kbd>V</kbd>.
              </p>

              {previa && (
                <div className="previa-print">
                  <img src={previa} alt="Pré-visualização do print" />
                  <button type="button" className="ligacao"
                    onClick={() => setPrint(null)}>remover</button>
                </div>
              )}

              <p className="rodape-regra">
                Vai junto, sem você digitar: <strong>{local.pathname}</strong> ·
                janela {window.innerWidth}×{window.innerHeight} · versão{' '}
                <code>{versaoDoPacote()}</code> · seu usuário e a hora.
              </p>

              {erro ? <ErroDaApi erro={erro} /> : null}
              <div className="rodape-dialogo">
                <button type="button" className="secundario" onClick={fechar}>
                  Cancelar
                </button>
                <button type="button" onClick={() => void enviar()}
                  disabled={enviando || invalido}>
                  {enviando ? 'Enviando…' : 'Enviar relato'}
                </button>
              </div>
            </div>
          )}
        </Dialogo>
      )}
    </>
  );
}
